import { formatDuration, formatNumber } from '../../lib/formatters.js';
import { PAGE_SOURCE_LABELS } from './constants.js';

interface SourceFunnelRow {
  source: string;
  pageViewUsers: number;
  modalUsers: number;
  purchasedUsers: number;
  avgDwellTimeMs: number | null;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 購入ファネルの「ページ別ファネル」深掘り。page_url ベースで分類した源泉ページごとに pageView / モーダル表示 / 購入完了 / 滞在時間 / 各遷移率を表示する。
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDuration [calls] 滞在時間フォーマット
 */
export function PurchaseSourceFunnel({ data }: { data: Record<string, unknown>[] }) {
  const rows = data as unknown as SourceFunnelRow[];

  if (rows.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          データがありません
        </p>
      </div>
    );
  }

  const totals = rows.reduce(
    (acc, row) => ({
      pageViewUsers: acc.pageViewUsers + row.pageViewUsers,
      modalUsers: acc.modalUsers + row.modalUsers,
      purchasedUsers: acc.purchasedUsers + row.purchasedUsers,
    }),
    { pageViewUsers: 0, modalUsers: 0, purchasedUsers: 0 },
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card animate-fade-in-up">
          <p className="text-xs text-[var(--color-text-muted)] mb-1">ページ閲覧ユーザー</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)]">
            {formatNumber(totals.pageViewUsers)}
          </p>
        </div>
        <div className="card animate-fade-in-up">
          <p className="text-xs text-[var(--color-text-muted)] mb-1">モーダル表示</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)]">
            {formatNumber(totals.modalUsers)}
          </p>
        </div>
        <div className="card animate-fade-in-up">
          <p className="text-xs text-[var(--color-text-muted)] mb-1">購入完了</p>
          <p className="text-2xl font-bold text-[var(--color-accent-primary)]">
            {formatNumber(totals.purchasedUsers)}
          </p>
        </div>
      </div>

      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          ページ別購入ファネル
        </h3>
        <p className="text-xs text-[var(--color-text-muted)] mb-4">
          page_url からユーザーがどのページで購入フローに入ったかを分類。
          タップ→モーダル表示→購入完了の各ステップをページ別に集計。
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  Source
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  ページ閲覧
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  平均滞在時間
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  モーダル表示
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  購入完了
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  閲覧→モーダル率
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  モーダル→購入率
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const source = row.source;
                const pageViews = row.pageViewUsers;
                const modal = row.modalUsers;
                const purchased = row.purchasedUsers;
                const viewToModal =
                  pageViews > 0 ? Math.round((modal / pageViews) * 1000) / 10 : null;
                const modalToPurchase =
                  modal > 0 ? Math.round((purchased / modal) * 1000) / 10 : null;

                return (
                  <tr
                    key={source}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="py-2.5 px-3 font-medium text-[var(--color-text-primary)]">
                      <span className="inline-flex items-center gap-1.5">
                        {source === 'home' && (
                          <span className="w-2 h-2 rounded-full bg-[var(--color-accent-primary)]" />
                        )}
                        {PAGE_SOURCE_LABELS[source] ?? source}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                      {formatNumber(pageViews)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-secondary)]">
                      {row.avgDwellTimeMs != null ? (
                        formatDuration(row.avgDwellTimeMs)
                      ) : (
                        <span className="text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                      {formatNumber(modal)}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-medium text-[var(--color-accent-primary)]">
                      {formatNumber(purchased)}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {viewToModal !== null ? (
                        <span className="text-[var(--color-text-secondary)]">
                          {String(viewToModal)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {modalToPurchase !== null ? (
                        <span
                          className={
                            modalToPurchase >= 50
                              ? 'text-[var(--color-positive)] font-medium'
                              : 'text-[var(--color-text-secondary)]'
                          }
                        >
                          {String(modalToPurchase)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[var(--color-border)] font-medium">
                <td className="py-2.5 px-3 text-[var(--color-text-primary)]">合計</td>
                <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                  {formatNumber(totals.pageViewUsers)}
                </td>
                <td className="py-2.5 px-3 text-right text-[var(--color-text-muted)]">—</td>
                <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                  {formatNumber(totals.modalUsers)}
                </td>
                <td className="py-2.5 px-3 text-right font-mono font-medium text-[var(--color-accent-primary)]">
                  {formatNumber(totals.purchasedUsers)}
                </td>
                <td className="py-2.5 px-3 text-right text-[var(--color-text-muted)]">—</td>
                <td className="py-2.5 px-3 text-right">
                  {totals.modalUsers > 0 ? (
                    <span className="text-[var(--color-text-secondary)]">
                      {String(Math.round((totals.purchasedUsers / totals.modalUsers) * 1000) / 10)}%
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
